import React from "react";
import { Link } from "react-router-dom";
import { LongPage, TocEntry } from "./SideNav";
import {
  Bullet,
  Bullets,
  DefItem,
  DefList,
  Ext,
  Mail,
  Note,
  PageHeader,
  Pull,
  Section,
  SimpleTable,
  SubHeading,
} from "./ui";
import { CONTACT_EMAIL, LINKS } from "./constants";

const TOC: TocEntry[] = [
  { id: "purpose", label: "What it is for" },
  { id: "roles", label: "Roles and departments" },
  { id: "getting-started", label: "Getting started" },
  { id: "researchers", label: "Everyday research work" },
  { id: "leading", label: "Running projects" },
  { id: "uploading", label: "Uploading data" },
  { id: "worth-knowing", label: "Worth knowing" },
  { id: "trouble", label: "If something looks wrong" },
  { id: "help", label: "Getting help" },
];

export const AboutPage: React.FC = () => (
  <LongPage
    toc={TOC}
    intro={
      <PageHeader
        kicker="About the system"
        title="What Nafnleyndarkerfi is, and how to use it"
        lead={
          <>
            <p className="mb-4">
              Written for department heads, principal investigators, researchers, and the
              hospital and registry staff who supply data. No technical background is assumed
              anywhere on this page.
            </p>
            <p>
              If you want the technical details of how the protection works, see{" "}
              <Link to="/security" className="text-accent underline underline-offset-2">
                Security
              </Link>
              . For what is built today and what is coming, see the{" "}
              <Link to="/roadmap" className="text-accent underline underline-offset-2">
                roadmap
              </Link>
              .
            </p>
          </>
        }
      />
    }
  >
    <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
      <Section id="purpose" number="01" title="What the system is for">
        <p>
          Health research data contains direct identifiers — kennitölur, names, addresses, phone
          numbers, email addresses. Researchers need the data, but they do not need to know who
          the people are.
        </p>
        <p>
          Nafnleyndarkerfi sits between the two. When data enters the system, every direct
          identifier is replaced with a <strong>pseudonym</strong> — a short code that says
          nothing about the person behind it. The real values are encrypted and stored
          separately, under their own keys. Researchers work with the codes.
        </p>

        <Pull>
          A pseudonym is stable inside a project, and different in every other project.
        </Pull>

        <Bullets>
          <Bullet>
            Inside project A, patient X is <em>always</em> the same code. Two datasets in project
            A can be linked on that code, and two researchers in project A can talk about
            &ldquo;subject <code>bK9x…</code>&rdquo; and mean the same person.
          </Bullet>
          <Bullet>
            In project B, that same patient X gets a completely different code. Project A and
            project B cannot combine their data, cross-reference their subjects, or discover that
            they share a participant — even if they hold the exact same source file.
          </Bullet>
        </Bullets>

        <p>
          That is on purpose. People inside one approved project can work together, and separate
          projects stay completely apart. Each project has its own key, so it gets its own
          codes.
        </p>

        <SubHeading>What the system does not do</SubHeading>
        <Bullets>
          <Bullet>
            <strong>It does not make data anonymous.</strong> Replacing identifiers hides who
            someone is, but it does not hide everything. A dataset with three patients who share
            a rare diagnosis is still sensitive, because the data itself can point back at them.
            Your ethical and legal duties for the data do not go away.
          </Bullet>
          <Bullet>
            <strong>It does not replace your approvals.</strong> Your IRB or ethics approval, your
            data processing agreement, and your obligations under the{" "}
            <Ext href={LINKS.gdpr}>GDPR</Ext> are all still yours. The system is a tool that
            helps you keep them, nothing more.
          </Bullet>
          <Bullet>
            <strong>It does not decide who may see what.</strong> People do — you and your PIs,
            through the permissions described below.
          </Bullet>
        </Bullets>
      </Section>

      <Section id="roles" number="02" title="Roles and departments">
        <p>
          There are four kinds of account. Your role decides what you see when you log in.
        </p>

        <DefList>
          <DefItem term="Researcher">
            The default. Works inside the projects they have been added to: browses the dataset
            catalogue, downloads what they are allowed to download, requests what they are not,
            and can pseudonymize their own files.
          </DefItem>
          <DefItem term="Principal investigator">
            Runs one or more projects. Everything a researcher can do, plus: create projects,
            invite people, set project policy, review and approve or reject data requests, upload
            data into their projects, and forward requests to external data providers.
          </DefItem>
          <DefItem term="Manager">
            Runs one department and oversees all of it — every project, every dataset in the
            catalogue, every data request, and every user in it. <strong>Deliberately cannot download any data.</strong> This is
            the intended role for a department head who needs oversight and reporting without
            taking on access to research data. Managers also look after the user accounts —
            promoting researchers to PI, disabling accounts, and resetting two-factor
            authentication for people who are locked out. A manager&rsquo;s reach stops at the
            edge of their own department.
          </DefItem>
          <DefItem term="Data provider">
            External staff, usually at a hospital or a registry, who supply data. They see only
            their own portal, which holds the requests addressed to them and the upload form.
            They have no access to the catalogue, to projects, or to anyone&rsquo;s results.
          </DefItem>
        </DefList>

        <SubHeading>Departments are separate worlds</SubHeading>
        <p>
          Everyone belongs to exactly one <strong>department</strong> — a faculty department, an
          institute, or a research unit. Projects, datasets, requests, and users all live inside
          one department, and nothing crosses between them. Health sciences and social sciences
          use the same software and share nothing else. Neither one can even see that the other
          exists, let alone what it holds.
        </p>
        <p>
          A manager runs their own department. They add principal investigators, oversee the
          projects, and look after the user accounts. What a manager cannot do is create a new
          department. Those are set up centrally, on request, so the line between departments is
          never something an account holder can move.
        </p>
        <Note tone="accent" title="Setting up a new department">
          <p>
            If your department, institute, or research unit would like its own space in the
            system, write to <Mail address={CONTACT_EMAIL} subject="New department in Nafnleyndarkerfi" />.
            Tell us who will act as manager, and we will set it up and send them an
            invitation.
          </p>
        </Note>
        <Note tone="neutral">
          <p>
            One note on wording. Parts of the interface call your department your{" "}
            <em>organization</em>, because that is the word the underlying system uses. They mean
            the same thing — the separate space your projects and data live in.
          </p>
        </Note>
      </Section>

      <Section id="getting-started" number="03" title="Getting started">
        <SubHeading>Your account</SubHeading>
        <p>
          Accounts are <strong>created by invitation</strong>. A PI or a manager sends an
          invitation to your email address. You follow the link in it and set a password. You
          cannot sign yourself up, and an invitation sent to one address cannot be used from
          another one.
        </p>
        <p>
          Your password must be at least 8 characters long and contain an uppercase letter, a
          lowercase letter, and a digit. It is also checked against{" "}
          <Ext href={LINKS.pwnedPasswords}>Pwned Passwords</Ext>, a public list of passwords that
          have leaked in known breaches. If yours is on that list you have to pick another one.
          The check is done in a way that never reveals your password to anyone. You also cannot
          reuse any of your last five passwords.
        </p>

        <SubHeading>Two-factor authentication is mandatory</SubHeading>
        <p>
          There is no way around it and no opt-out. The first time you log in you are shown a QR
          code. Scan it with an authenticator app, then type in the 6-digit code to confirm.
          Until you finish that, the system will not let you do anything else.
        </p>
        <p>
          Any app implementing the <Ext href={LINKS.totpRfc}>TOTP standard</Ext> works. Make sure to
          install from the official page:
        </p>
        <Bullets tight>
          <Bullet>
            <Ext href={LINKS.googleAuthenticator}>Google Authenticator</Ext> — Google&rsquo;s own
            download and setup page
          </Bullet>
          <Bullet>
            <Ext href={LINKS.microsoftAuthenticator}>Microsoft Authenticator</Ext> — microsoft.com
          </Bullet>
          <Bullet>
            <Ext href={LINKS.authy}>Authy</Ext> — by Twilio
          </Bullet>
          <Bullet>
            <Ext href={LINKS.onePassword}>1Password</Ext> — if you already use it as a password
            manager, it stores the code alongside the password
          </Bullet>
          <Bullet>
            <Ext href={LINKS.freeOtp}>FreeOTP</Ext> — open source, by Red Hat
          </Bullet>
        </Bullets>
        <Note tone="caution" title="Save your recovery route now">
          <p>
            There are no printable backup codes. If you lose your phone, a manager has to reset
            your second factor for you, and you then set it up again on your new device. Make a
            note of who your manager is.
          </p>
        </Note>

        <SubHeading>Logging in</SubHeading>
        <p>
          Email, password, then the 6-digit code from your app. Five failed attempts from the same
          network lock logins for five minutes.
        </p>
        <p>
          Your session stays alive as long as you are working. The clock counts{" "}
          <em>idle</em> time, not total time, so you will not be logged out in the middle of a
          task. After roughly 15 minutes without activity the session ends and you are asked to
          log in again.
        </p>

        <SubHeading>If you forget your password</SubHeading>
        <p>
          Use the &ldquo;Forgot password&rdquo; link. You get a reset link that works for 15
          minutes, and you can ask for at most three of them per hour. If your installation
          cannot send email yet, contact your manager instead. They can reset the account for
          you.
        </p>
        <p>
          You can change your password yourself at any time under Settings. Because that is a
          security change, you are asked for your current password <em>and</em> your 6-digit
          code.
        </p>
      </Section>

      <Section id="researchers" number="04" title="Everyday research work">
        <SubHeading>Your projects</SubHeading>
        <p>
          The projects page lists the projects you belong to and your role in each one. Everything
          you do belongs to one project, and the project is what decides which codes your data
          gets.
        </p>

        <SubHeading>Browsing the dataset catalogue</SubHeading>
        <p>The catalogue shows the datasets available to you. For each one you can see:</p>
        <Bullets tight>
          <Bullet>name, description, who uploaded it, and when</Bullet>
          <Bullet>the number of rows</Bullet>
          <Bullet>
            a list of the columns and their types, plus summary numbers such as ranges, averages,
            and category lists for the columns that are not sensitive
          </Bullet>
          <Bullet>whether the dataset expires, and when</Bullet>
          <Bullet>whether you can download it directly, or must request it</Bullet>
        </Bullets>
        <Note tone="accent" title="Sensitive columns are shown by name only">
          <p>
            No values, no statistics, no example categories. The catalogue is designed so that
            browsing it cannot leak personal data.
          </p>
        </Note>

        <p>Two separate settings control each dataset, and they work independently of each
          other:</p>
        <SimpleTable
          head={["", "What it controls"]}
          rows={[
            [
              "Visibility",
              "Whether the dataset appears in your catalogue at all. It may be shared with your whole department, with specific projects, or hidden.",
            ],
            [
              "Download",
              "Whether you can take the data without asking. It may be open to a project's members, restricted to named individuals, or closed to everyone.",
            ],
          ]}
        />
        <p>
          So a dataset can be visible without being downloadable. That is the normal case for data
          that needs PI approval. A dataset can also be downloadable by you and invisible to a
          colleague. Neither of those means something is broken.
        </p>

        <SubHeading>Downloading a dataset</SubHeading>
        <p>
          If a dataset is marked as downloadable for you, download it straight from the catalogue.
          What you get is a <strong>pseudonymized CSV</strong> with the codes of the project you
          downloaded it in. A dataset belongs to a project and has to be downloaded from inside
          that project. If you need the same data for a different project, talk to your PI. It
          will get different codes there, and that is intended.
        </p>

        <SubHeading>Downloading several datasets at once</SubHeading>
        <p>Select up to 50 datasets and use bulk download. You get a single ZIP containing:</p>
        <Bullets tight>
          <Bullet>one pseudonymized CSV per dataset</Bullet>
          <Bullet>
            a ready-to-run <Ext href={LINKS.jupyter}>Jupyter</Ext> notebook that loads them all
          </Bullet>
          <Bullet>
            a ready-to-run <Ext href={LINKS.rProject}>R</Ext> script that does the same
          </Bullet>
          <Bullet>a README listing what is inside</Bullet>
        </Bullets>
        <p>
          All the files use the same project&rsquo;s codes, so the same subject has the same code
          in every file and you can link them directly. If even one dataset in your selection is
          not downloadable by you, the whole download is refused. You never get a partial archive
          without being told.
        </p>

        <SubHeading>Requesting data you cannot download</SubHeading>
        <p>
          For anything else, submit a <strong>data request</strong>. You can start it from the
          dataset itself, or on its own if the data is not in the system yet. You give it a title
          and a purpose, say which dataset and which columns you need, and add any filters or
          processing notes.
        </p>
        <p>
          The request then goes to your PI. You can follow its status, add comments, edit it, or
          cancel it while it is still pending, and you are notified whenever anything
          changes.
        </p>
        <SimpleTable
          head={["Status", "Meaning"]}
          rows={[
            ["Pending", "Waiting for review."],
            ["Approved", "The PI said yes, and the data is being prepared."],
            ["Rejected", "Declined, with the reviewer's reason."],
            ["Redirected", "Forwarded to an external data provider who holds the data."],
            ["Fulfilled", "The data is available to you."],
          ]}
        />

        <SubHeading>Pseudonymizing your own file</SubHeading>
        <p>
          If you already have a file and just want the identifiers replaced, use the CSV tool
          inside your project. Upload the file, pick which columns hold identifiers, and download
          the pseudonymized result. The codes come from your project, so they match everything
          else in it.
        </p>

        <SubHeading>Uploading data into a project</SubHeading>
        <p>
          PIs, and researchers where the project allows it, can upload a dataset directly. The
          upload goes through a short review step, described in{" "}
          <a href="#uploading">Uploading data</a> below. You choose who can see it in the
          catalogue, who can download it, and optionally an expiry date.
        </p>
      </Section>

      <Section id="leading" number="05" title="Running projects and overseeing a department">
        <SubHeading>Creating and running a project</SubHeading>
        <p>
          A PI creates a project in the interface by giving it a title and a description, and
          becomes the PI of that project. From the project page you manage members, settings,
          datasets, and requests.
        </p>

        <SubHeading>Bringing people in</SubHeading>
        <p>
          Invite people by email address from the project page and choose their role in the
          project, either researcher or PI. If they already have an account in your department,
          accepting the invitation adds them to the project. If they do not, the invitation walks
          them through creating an account first.
        </p>
        <p>
          You can preview the invitation before sending it, add a personal message, see which
          invitations are still outstanding, and withdraw one that has not been used. A project
          must always have at least one PI, so the system refuses any removal that would leave it
          without one.
        </p>

        <SubHeading>Project settings</SubHeading>
        <p>
          Three settings, chosen per project, decide what happens by default when data is uploaded
          into it:
        </p>
        <DefList>
          <DefItem term="Researcher access">
            Either <em>require a data request</em>, so a PI reviews every access, which is the
            careful default, or <em>grant automatically</em>, so project members can download new
            datasets straight away. Choose per project, based on how sensitive its data is.
          </DefItem>
          <DefItem term="Catalogue visibility">
            Whether new datasets appear to this project only, to the whole department, or are
            hidden from the catalogue entirely.
          </DefItem>
          <DefItem term="Notify on upload">
            Whether the requester and the project&rsquo;s PIs are told automatically when data
            arrives.
          </DefItem>
        </DefList>
        <p>
          These are only the defaults for new uploads. Whoever uploads can still choose something
          stricter for a single dataset.
        </p>

        <SubHeading>Reviewing data requests</SubHeading>
        <p>
          Requests for your projects show up in your review queue. For each one you can read the
          whole request, discuss it with the requester in the comments, and leave internal notes
          that only other reviewers see. Then you can:
        </p>
        <Bullets tight>
          <Bullet>
            <strong>Approve</strong> it and fulfil it, by attaching the dataset and columns or by
            uploading a file.
          </Bullet>
          <Bullet>
            <strong>Reject</strong> it with a reason. The requester is notified and can read the
            reason.
          </Bullet>
          <Bullet>
            <strong>Redirect</strong> it to whoever actually holds the data.
          </Bullet>
        </Bullets>
        <p>
          Wherever the data comes from, it is pseudonymized with the{" "}
          <strong>requesting project&rsquo;s</strong> key when the request is fulfilled. The
          requester never sees real identifiers.
        </p>

        <SubHeading>Forwarding a request to a data provider</SubHeading>
        <p>
          When the data sits outside your department, at a hospital or a registry, redirect the
          request to one or more data providers or institutions. Each of them is tracked on its
          own and can accept, decline, or fulfil the request independently, so you can see
          exactly which sources have delivered and which have not. Having some of them delivered
          and others still pending is a normal state, not a stuck one.
        </p>

        <SubHeading>Overseeing a department</SubHeading>
        <p>
          A manager gets a dashboard covering the whole department — all its projects, all its
          users and which projects they belong to, all data requests and their status, and the
          full dataset catalogue. This is the view for reporting and auditing.
        </p>
        <Note tone="accent" title="Oversight does not carry data access">
          <p>
            A manager cannot download data. If you need both oversight and access to data, you also
            need the PI role on the specific projects whose data you work with. That is the whole
            idea. Access to data follows project membership, not seniority.
          </p>
        </Note>

        <SubHeading>Managing people</SubHeading>
        <p>Managers can:</p>
        <Bullets tight>
          <Bullet>
            promote a researcher to PI, or move a PI back to researcher, which also removes them as
            PI from their projects
          </Bullet>
          <Bullet>
            disable an account, which takes effect immediately and ends any session that is already
            open
          </Bullet>
          <Bullet>re-enable a disabled account</Bullet>
          <Bullet>
            end all of a user&rsquo;s sessions without disabling the account, for example if a
            device is lost
          </Bullet>
          <Bullet>
            reset two-factor authentication for someone who is locked out, after which they set it
            up again at their next login
          </Bullet>
        </Bullets>

        <SubHeading>Announcements</SubHeading>
        <p>
          PIs and managers can send a notification out to users, with formatting, headings, lists,
          and links. It shows up in the notification centre, and where email is set up it is sent
          by email too. The formatting is cleaned up before sending, so recipients see exactly
          what you wrote.
        </p>
      </Section>

      <Section id="uploading" number="06" title="Uploading data, and what the system checks for you">
        <p>
          Whether you are a researcher, a PI, or an external data provider, every upload goes
          through the same review step. <strong>Files must be CSV.</strong> Comma, semicolon,
          tab, and pipe separators are all detected automatically, so you do not need to convert
          anything. Excel files are not accepted, so export to CSV first. The largest file the
          system takes is 100 MB.
        </p>

        <SubHeading>The review step</SubHeading>
        <p>Before anything is stored, the system scans your file and shows you what it found.</p>
        <p>
          <strong>Identifier columns.</strong> These are columns that look like they hold personal
          data, judged both by their name (<code>kennitala</code>, <code>nafn</code>,{" "}
          <code>heimilisfang</code>, <code>email</code>, …) and by what is inside them. A valid
          kennitala, a formatted phone number, an email address, or an Icelandic patronymic
          surname is enough on its own, and a column that is mostly full names is flagged too.
          You confirm the list or adjust it, and the columns you confirm are pseudonymized.
        </p>
        <p>
          <strong>Personal data hidden inside free text.</strong> This is the one people miss. A
          &ldquo;notes&rdquo; or &ldquo;comments&rdquo; column is not an identifier column, but it
          can still have a kennitala buried in a sentence, as in{" "}
          <em>&ldquo;discussed with kt. 050385-2059 on Tuesday&rdquo;</em>. That is exactly the
          leak that protecting whole columns does not catch, so the system also reads inside such
          columns and shows you every match together with the text around it.
        </p>
        <p>For every finding you choose:</p>
        <Bullets tight>
          <Bullet>
            <strong>Replace it.</strong> The identifier inside the cell is swapped for a code and
            the rest of the sentence is kept. It becomes{" "}
            <em>the same</em> code that person has in your identifier columns, so the sentence
            still points at the right subject.
          </Bullet>
          <Bullet>
            <strong>Drop the column.</strong> If the column is not worth keeping, remove it
            entirely. Its original contents are then never stored anywhere.
          </Bullet>
        </Bullets>
        <Note tone="caution" title="Please actually read this screen">
          <p>
            Nothing is stored until you confirm. This is the last point where a mistake is still
            cheap to fix.
          </p>
        </Note>

        <SubHeading>Setting access</SubHeading>
        <p>
          When you upload, you choose who can see the dataset in the catalogue, who can download
          it without a request, and optionally an expiry date. Your project&rsquo;s defaults are
          filled in for you, and you can always choose something stricter.
        </p>

        <SubHeading>For external data providers</SubHeading>
        <p>Log in and you land on your portal, where you will see:</p>
        <Bullets tight>
          <Bullet>
            <strong>Requests directed to you.</strong> Accept the ones you can fulfil and decline
            the ones you cannot. Giving a reason helps the requester far more than silence.
          </Bullet>
          <Bullet>
            <strong>Accepted requests.</strong> Preview and upload the file for each one. The
            preview runs the same scan described above.
          </Bullet>
          <Bullet>
            <strong>Your activity.</strong> A log of everything you have uploaded, for your own
            records.
          </Bullet>
        </Bullets>
        <p>
          You never see research results, other providers&rsquo; data, or the catalogue. Your part
          ends when the file has been delivered.
        </p>
      </Section>

      <Section id="worth-knowing" number="07" title="Things worth knowing">
        <SubHeading>Expiry means the data is deleted</SubHeading>
        <p>
          A dataset can be given an expiry date, and that date is a deletion deadline, not just a
          display setting. The moment it passes, the dataset disappears from the catalogue{" "}
          <em>and</em> can no longer be downloaded by anyone, including the person who uploaded
          it and anyone who kept a direct link. Nothing can grant access to an expired
          dataset.
        </p>
        <p>
          Shortly afterwards the dataset is <strong>permanently deleted from the server</strong>.
          The stored file is erased and the encryption key for that dataset is destroyed, so
          nobody can put its contents back together. There is a short grace period between the
          expiry date and the deletion itself, because deleting cannot be undone and a mistyped
          date should be fixable. Throughout that grace period the dataset is already out of
          reach.
        </p>
        <p>
          What is left afterwards is the record — the dataset&rsquo;s name, who uploaded it, who
          used it while it existed, and the fact that it was destroyed and when. That record is
          what lets you show an ethics board or a data provider that you really did keep your
          promise about how long the data would be kept.
        </p>

        <SubHeading>What expiry does not delete</SubHeading>
        <p>
          Deleting a dataset does not delete your project&rsquo;s{" "}
          <strong>pseudonym mapping</strong>, the encrypted table that records which code stands
          for which person. That table belongs to the project, not to any single dataset, so it
          outlives the datasets built from it.
        </p>
        <p>
          That is on purpose, and here is what it makes possible. Say you downloaded a
          pseudonymized file last month, and the dataset behind it has since expired and been
          destroyed. An authorized lookup of the codes in your file still works. The data you
          were given does not become unreadable forever just because the source dataset reached
          its deletion deadline.
        </p>
        <p>
          So, put plainly: <strong>expiry destroys the dataset, not the identities behind
          it.</strong> The original identifier values stay, encrypted under a separate key, for
          as long as the project exists. Looking them up still requires administrator access and
          is still logged. Removing a project removes its mappings along with it.
        </p>
        <Note tone="accent" title="If you set an expiry date, trust it">
          <p>
            This is the whole point of the feature. Someone who uploads data, whether a hospital, a
            registry, or a colleague, and sets a deletion deadline should not have to hope that
            somebody remembers to honour it. The system does it for them.
          </p>
        </Note>

        <SubHeading>Everything is logged</SubHeading>
        <p>
          Logins, failed logins, logins from a new location, file uploads, dataset downloads (who,
          when, and which columns), submitted requests and the decisions on them, and changes to
          permissions and roles are all recorded. That is on purpose, and it is what lets you
          answer an ethics board or a regulator with evidence. Assume that every access can be
          traced back to you.
        </p>

        <SubHeading>Rate limits</SubHeading>
        <p>
          There are sensible ceilings in place to stop abuse — 5 login attempts per 5 minutes, 10
          data requests per hour, 3 password resets per hour. Normal use never comes close to
          them. If you see &ldquo;too many requests&rdquo;, just wait the time it tells you
          to.
        </p>

        <SubHeading>Notifications</SubHeading>
        <p>
          The bell in the top bar collects request status changes, uploads into your projects,
          invitations, and announcements. You can mark them read one by one or all at once. Where
          email is set up, the important ones arrive by email as well.
        </p>

        <SubHeading>Pseudonyms are reversible — by design, and under control</SubHeading>
        <p>
          The original values are encrypted and kept, so an authorized administrator can look up
          who a code belongs to when there is a good, documented reason — a safety finding that
          has to reach a clinician, for example. This is not a back door. It takes administrator
          access, and every lookup is logged. How far that administrator trust currently reaches,
          and where it stops, is spelled out under{" "}
          <Link to="/security#review" className="text-accent underline underline-offset-2">
            what is not solved
          </Link>
          .
        </p>
      </Section>

      <Section id="trouble" number="08" title="If something looks wrong">
        <SimpleTable
          head={["Situation", "What to do"]}
          rows={[
            [
              "Locked out of two-factor authentication",
              "Contact your manager. They reset it, and you set it up again at your next login.",
            ],
            [
              "Forgot your password",
              "Use “Forgot password”, or ask your manager if email is not set up yet.",
            ],
            ["“Too many requests”", "Wait the time stated, then retry."],
            [
              "A dataset is visible but you cannot download it",
              "That is normal. Submit a data request instead.",
            ],
            [
              "You can see a dataset your colleague cannot",
              "Also normal. Visibility is set per project and per person.",
            ],
            ["Upload rejected", "Check that it is a CSV and under 100 MB."],
            [
              "Codes do not match between two files",
              "Check that both files came from the same project. Different projects always give different codes.",
            ],
            [
              "You think you have access you should not have",
              "Report it to your manager immediately.",
            ],
          ]}
        />
      </Section>

      <Section id="help" number="09" title="Getting help">
        <p>
          For anything about your account, your access, or a dataset, start with your
          project&rsquo;s principal investigator or your department&rsquo;s manager. They can
          sort out most of it themselves, including password and two-factor resets.
        </p>
        <p>
          For anything they cannot sort out — a new department, a bug, a feature you need, or a
          question about how the system handles your data — write to{" "}
          <Mail address={CONTACT_EMAIL} />. A plain email is enough, there is no form to fill
          in.
        </p>
        <p>
          If something looks like a security problem, report it to your manager immediately rather
          than investigating it yourself.
        </p>
      </Section>
    </div>
  </LongPage>
);
